import React, { useEffect, useMemo, useState } from "react";
import { View, Pressable } from "react-native-web";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";

import CustomText from "../atoms/CustomText";
import { colors, spacing } from "../../utils/styling";
import { useResponsive } from "../../hooks/useResponsive";
import { Exercise } from "../../utils/category_mappings";
import {
  useExerciseResponses,
  useSaveExerciseResponse,
} from "../../lib/exercises";
import {
  MapBody,
  ActionPlanBody,
  TrackerBody,
  RankingBody,
  isMapComplete,
  isActionPlanComplete,
  isTrackerComplete,
  isRankingComplete,
  MapResponse,
  ActionPlanResponse,
  TrackerResponse,
  RankingResponse,
} from "./exercises/ExerciseBodies";

// Interactive workbook — the per-personality 5-Step Guided Action Plan
// (ClickUp 86badfk6e, P3 of the advisor-portal pivot).
//
// All FIVE exercise types are interactive + persisted per quiz via the
// /exercises API, with a green checkmark marking completion:
//   1. Reflective prompt   — free-text reflection + helpful-tips sidebar + footer
//   2. Visualization map    — place-and-label board (drag items, label your why)
//   3. Action plan          — structured form parsed from the prompt's fields
//   4. Behavior tracker      — ~3-day daily log
//   5. Insight ranking       — drag-and-drop priority ordering
// The response shape varies per type (free text, placements, fields, day-log,
// ranking) but the backend `response` column is schemaless jsonb, so every type
// reuses the identical save mechanism.

// Stable keys per exercise id (also used as the backend `exercise_key`).
const EXERCISE_KEYS: Record<number, string> = {
  1: "reflective_prompt",
  2: "visualization_map",
  3: "action_plan",
  4: "behavior_tracker",
  5: "insight_ranking",
};

// All five exercise types are interactive.
const INTERACTIVE_IDS = new Set<number>([1, 2, 3, 4, 5]);

// Empty default response per exercise id (seeds the dialog before any answer).
function emptyResponse(id: number): Record<string, any> {
  switch (id) {
    case 1:
      return { text: "" };
    case 2:
      return { placements: [] };
    case 3:
      return { fields: {} };
    case 4:
      return { days: [] };
    case 5:
      return { ranking: [] };
    default:
      return {};
  }
}

// Derive the completion flag from a response, per exercise type.
function computeCompleted(id: number, response: Record<string, any>): boolean {
  switch (id) {
    case 1:
      return ((response.text as string) || "").trim().length > 0;
    case 2:
      return isMapComplete(response as MapResponse);
    case 3:
      return isActionPlanComplete(response as ActionPlanResponse);
    case 4:
      return isTrackerComplete(response as TrackerResponse);
    case 5:
      return isRankingComplete(response as RankingResponse);
    default:
      return false;
  }
}

const CARD_COLORS = ["#E6B3D1", "#B19CD9", "#AED6F1", "#A8D8A8", "#7FB3C3"];

interface GuidedActionPlanProps {
  personality: string;        // canonical main name (e.g. "Safety Seeker")
  exercises: Exercise[];      // the 5 exercises for this sub-personality
  quizId?: number;            // the taker's quiz; required to persist answers
}

export default function GuidedActionPlan({
  personality,
  exercises,
  quizId,
}: GuidedActionPlanProps) {
  const { isMobile } = useResponsive();

  const { data: responses, isLoading: responsesLoading } =
    useExerciseResponses(quizId);
  const saveResponse = useSaveExerciseResponse();

  const [openExerciseId, setOpenExerciseId] = useState<number | null>(null);
  const [draftResponse, setDraftResponse] = useState<Record<string, any>>({});
  // Tracks whether the user has edited the draft since opening the dialog, so a
  // late-arriving server load doesn't clobber what they entered.
  const [draftDirty, setDraftDirty] = useState(false);

  // Map exercise_key -> saved response object + completed, for checkmarks + prefill.
  const responsesByKey = useMemo(() => {
    const map: Record<string, { response: Record<string, any>; completed: boolean }> = {};
    (responses || []).forEach((r) => {
      map[r.exercise_key] = {
        response: (r.response as Record<string, any>) || {},
        completed: r.completed,
      };
    });
    return map;
  }, [responses]);

  const openExercise = exercises.find((e) => e.id === openExerciseId) || null;
  const openExerciseKey = openExercise ? EXERCISE_KEYS[openExercise.id] : null;

  // Seed / re-sync the draft from the saved answer. Depends on responsesByKey
  // (not just openExerciseId) so that if the dialog opens BEFORE the responses
  // query resolves, the saved answer fills in once it loads — but only while the
  // user hasn't started editing, so we never overwrite their in-progress work.
  useEffect(() => {
    if (!openExercise || !openExerciseKey || draftDirty) return;
    const saved = responsesByKey[openExerciseKey]?.response;
    setDraftResponse(saved && Object.keys(saved).length > 0 ? saved : emptyResponse(openExercise.id));
  }, [openExercise, openExerciseKey, responsesByKey, draftDirty]);

  const handleOpen = (id: number) => {
    if (!INTERACTIVE_IDS.has(id)) return;
    setDraftDirty(false);
    setDraftResponse(emptyResponse(id));
    setOpenExerciseId(id);
  };

  const handleClose = () => setOpenExerciseId(null);

  // Body components emit the next response object; mark the draft dirty so a
  // late server load can't clobber it.
  const updateDraft = (next: Record<string, any>) => {
    setDraftDirty(true);
    setDraftResponse(next);
  };

  const handleSave = async () => {
    if (!openExercise || !quizId) {
      handleClose();
      return;
    }
    const exercise_key = EXERCISE_KEYS[openExercise.id];
    try {
      await saveResponse.mutateAsync({
        quizId,
        personality,
        exercise_key,
        response: draftResponse,
        completed: computeCompleted(openExercise.id, draftResponse),
      });
      handleClose();
    } catch (e) {
      // Surfaced via the disabled/spinner state; leave the dialog open so the
      // user can retry without losing their work.
      console.error("Failed to save exercise response:", e);
    }
  };

  const completedCount = exercises.filter(
    (e) => responsesByKey[EXERCISE_KEYS[e.id]]?.completed
  ).length;

  return (
    <View
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: colors.white,
        border: "3px solid black",
        borderRadius: 16,
        padding: isMobile ? spacing.lg : spacing.xl,
        marginBottom: isMobile ? spacing.xl : spacing.md,
      }}
    >
      <CustomText
        style={{
          fontFamily: "Nunito",
          fontSize: isMobile ? 36 : 48,
          color: "black",
          fontWeight: 700,
          marginBottom: isMobile ? spacing.xs : spacing.sm,
          textAlign: "center",
        }}
      >
        5-Step Guided Action Plan
      </CustomText>
      <CustomText
        style={{
          fontFamily: "Nunito",
          fontSize: isMobile ? 16 : 20,
          color: colors.gray[600],
          marginBottom: isMobile ? spacing.lg : spacing.xl,
          fontWeight: 400,
          textAlign: "center",
        }}
      >
        Practical, tailored steps to grow as a {personality}. {completedCount} of{" "}
        {exercises.length} complete — your answers are saved automatically.
      </CustomText>

      <View
        style={{
          width: "100%",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          flexWrap: "wrap",
          gap: isMobile ? spacing.md : spacing.lg,
          justifyContent: "center",
        }}
      >
        {exercises.map((exercise, idx) => {
          const key = EXERCISE_KEYS[exercise.id];
          const done = responsesByKey[key]?.completed;
          const interactive = INTERACTIVE_IDS.has(exercise.id);
          return (
            <Pressable
              key={exercise.id}
              onPress={() => handleOpen(exercise.id)}
              style={{
                backgroundColor: CARD_COLORS[idx % CARD_COLORS.length],
                borderRadius: 20,
                padding: isMobile ? spacing.lg : spacing.xl,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: isMobile ? 140 : 180,
                width: isMobile ? "100%" : 280,
                cursor: interactive ? "pointer" : "default",
                opacity: interactive ? 1 : 0.85,
                position: "relative",
                border: done ? "3px solid #00AE23" : "3px solid transparent",
              }}
            >
              {done && (
                <CustomText
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 14,
                    fontSize: 26,
                    color: "#00AE23",
                  }}
                >
                  ✓
                </CustomText>
              )}
              <CustomText
                style={{
                  fontFamily: "Nunito",
                  fontSize: isMobile ? 24 : 28,
                  fontWeight: 700,
                  color: "#444",
                  textAlign: "center",
                  marginBottom: spacing.xs,
                }}
              >
                Exercise {exercise.id}: {exercise.title}
              </CustomText>
              <CustomText
                style={{
                  fontFamily: "Nunito",
                  fontSize: isMobile ? 14 : 16,
                  fontWeight: 600,
                  color: "#555",
                  textAlign: "center",
                }}
              >
                {interactive
                  ? done
                    ? "Tap to review your answer"
                    : "Tap to start"
                  : "Coming soon"}
              </CustomText>
            </Pressable>
          );
        })}
      </View>

      {/* Reflective-prompt exercise dialog (Phase 1 interactive type) */}
      <Dialog open={!!openExercise} onClose={handleClose} maxWidth="md" fullWidth>
        {openExercise && (
          <>
            <DialogTitle sx={{ fontFamily: "Nunito", fontWeight: 700 }}>
              {openExercise.header}
            </DialogTitle>
            <DialogContent>
              <CustomText
                style={{
                  fontFamily: "Nunito",
                  fontSize: 16,
                  color: colors.gray[700],
                  marginBottom: spacing.md,
                }}
              >
                {openExercise.intro}
              </CustomText>

              <View
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: spacing.lg,
                }}
              >
                {/* Interaction area — swaps by exercise type */}
                <View style={{ flex: 2, minWidth: 0 }}>
                  {/* Exercise 1 (reflective prompt) shows the prompt as the
                      field label; types 2–5 surface the prompt as context above
                      their own structured interaction. */}
                  <CustomText
                    style={{
                      fontFamily: "Nunito",
                      fontSize: 16,
                      fontWeight: openExercise.id === 1 ? 700 : 400,
                      color: openExercise.id === 1 ? colors.dark : colors.gray[600],
                      marginBottom: spacing.sm,
                    }}
                  >
                    {openExercise.prompt}
                  </CustomText>

                  {openExercise.id === 1 && (
                    <TextField
                      value={(draftResponse.text as string) || ""}
                      onChange={(e) => updateDraft({ text: e.target.value })}
                      placeholder="Write your reflection here…"
                      multiline
                      minRows={6}
                      fullWidth
                      variant="outlined"
                    />
                  )}
                  {openExercise.id === 2 && (
                    <MapBody
                      exercise={openExercise}
                      value={draftResponse as MapResponse}
                      onChange={updateDraft}
                    />
                  )}
                  {openExercise.id === 3 && (
                    <ActionPlanBody
                      exercise={openExercise}
                      value={draftResponse as ActionPlanResponse}
                      onChange={updateDraft}
                    />
                  )}
                  {openExercise.id === 4 && (
                    <TrackerBody
                      exercise={openExercise}
                      value={draftResponse as TrackerResponse}
                      onChange={updateDraft}
                    />
                  )}
                  {openExercise.id === 5 && (
                    <RankingBody
                      exercise={openExercise}
                      value={draftResponse as RankingResponse}
                      onChange={updateDraft}
                    />
                  )}
                </View>

                {/* Helpful tips sidebar */}
                <View
                  style={{
                    flex: 1,
                    minWidth: 0,
                    backgroundColor: "#fff7da",
                    border: "2px solid #f4e67d",
                    borderRadius: 12,
                    padding: spacing.md,
                  }}
                >
                  <CustomText
                    style={{
                      fontFamily: "Nunito",
                      fontSize: 16,
                      fontWeight: 700,
                      color: colors.dark,
                      marginBottom: spacing.sm,
                    }}
                  >
                    💡 Helpful Tips
                  </CustomText>
                  {openExercise.tips.map((tip, i) => (
                    <CustomText
                      key={i}
                      style={{
                        fontFamily: "Nunito",
                        fontSize: 14,
                        color: colors.gray[700],
                        marginBottom: spacing.xs,
                      }}
                    >
                      • {tip}
                    </CustomText>
                  ))}
                </View>
              </View>

              {openExercise.footer ? (
                <CustomText
                  style={{
                    fontFamily: "Nunito",
                    fontSize: 15,
                    fontStyle: "italic",
                    color: colors.gray[600],
                    marginTop: spacing.md,
                  }}
                >
                  {openExercise.footer}
                </CustomText>
              ) : null}

              {!quizId && (
                <CustomText
                  style={{
                    fontFamily: "Nunito",
                    fontSize: 13,
                    color: colors.danger,
                    marginTop: spacing.sm,
                  }}
                >
                  Saving is unavailable for this session.
                </CustomText>
              )}
            </DialogContent>
            <DialogActions>
              <Pressable onPress={handleClose} style={{ padding: spacing.sm }}>
                <CustomText style={{ fontFamily: "Nunito", color: colors.gray[600] }}>
                  Close
                </CustomText>
              </Pressable>
              <Pressable
                onPress={handleSave}
                disabled={saveResponse.isPending || !quizId || responsesLoading}
                style={{
                  backgroundColor: colors.primary,
                  borderRadius: 8,
                  paddingVertical: spacing.sm,
                  paddingHorizontal: spacing.lg,
                  opacity:
                    saveResponse.isPending || !quizId || responsesLoading ? 0.6 : 1,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: spacing.xs,
                }}
              >
                {saveResponse.isPending && (
                  <CircularProgress size={16} color="inherit" />
                )}
                <CustomText
                  style={{ fontFamily: "Nunito", color: colors.white, fontWeight: 600 }}
                >
                  Save my answer
                </CustomText>
              </Pressable>
            </DialogActions>
          </>
        )}
      </Dialog>
    </View>
  );
}
